import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, ArrowUpRight, ShieldCheck, Heart, Building2, Stethoscope, Pill, FlaskConical, Package, HeartPulse, Ambulance, Droplet, CalendarHeart, RotateCcw, HeartHandshake } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { TYPE_META, type ListingV2 } from "@/lib/db/listings";
import { useUIConfig, getShadowClass } from "@/hooks/useUIConfig";
import { useProviderLikes } from "@/hooks/useProviderLikes";
import { useCategoryPlaceholder } from "@/lib/categoryPlaceholders";
import { usePatientAuth } from "@/contexts/PatientAuthContext";
import { useAuthConfig } from "@/hooks/useAuthConfig";
import PatientAuthModal from "@/components/patient/PatientAuthModal";
import { ambulanceTypeLabel } from "@/lib/ambulanceTypes";

interface ReturnRouteMatch {
  fromCity?: string;
  toCity?: string;
  ambulanceType?: string;
  discountRate?: number;
}

interface Props {
  item: ListingV2;
  /** Set when this card's ambulance has an active return trip matching the
   * searched From/To city — highlights that specific route instead of the
   * generic "first" one. */
  matchedReturnRoute?: ReturnRouteMatch | null;
}

const TYPE_ICON: Record<number, any> = {
  1: Building2,
  2: Stethoscope,
  3: Pill,
  4: HeartPulse,
  5: HeartPulse,
  6: FlaskConical,
  7: Package,
  8: Droplet,
  9: HeartPulse,
  10: Ambulance,
};

const TYPE_BADGE_COLOR: Record<number, string> = {
  1: "#1E5AA8",
  2: "#0EA5E9",
  3: "#16A34A",
  4: "#EC4899",
  5: "#8B5CF6",
  6: "#7C3AED",
  7: "#D97706",
  8: "#DC2626",
  9: "#059669",
  10: "#EA580C",
};



function RatingStars({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const stars = [] as JSX.Element[];
  for (let i = 0; i < fullStars && i < 5; i++) {
    stars.push(<Star key={i} className="w-3 h-3 text-amber-500 fill-amber-500" />);
  }
  if (hasHalf && stars.length < 5) {
    stars.push(<Star key="half" className="w-3 h-3 text-amber-500 fill-amber-500/50" />);
  }
  while (stars.length < 5) {
    stars.push(<Star key={`e${stars.length}`} className="w-3 h-3 text-muted-foreground/30" />);
  }
  return (
    <div className="flex items-center gap-0.5">
      {stars}
      <span className="text-xs font-bold text-foreground ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

const toArr = (v: any): string[] => {
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (typeof v === "string" && v.trim()) return v.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
};
/** Small category-specific info strip shown on specialized provider cards. */
function TypeStrip({ item, matchedReturnRoute }: { item: ListingV2; matchedReturnRoute?: ReturnRouteMatch | null }) {
  const d = item.details ?? {};
  if (item.type_code === 8) {
    const groups = toArr(d.blood_types ?? d.blood_groups);
    return (
      <div className="mb-1.5 flex flex-wrap items-center gap-1">
        <span className="inline-flex items-center gap-1 rounded-full border border-rose-600/20 bg-rose-600/10 px-2 py-0.5 text-[11px] font-medium text-rose-700">
          <Droplet className="h-3 w-3" /> {d.emergency ? "24x7 Blood Supply" : "Blood Bank"}
        </span>
        {groups.slice(0, 3).map((g) => (
          <span key={g} className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-bold">{g}</span>
        ))}
      </div>
    );
  }
  if (item.type_code === 10) {
    // Real per-vehicle return routes (from formatListingResponse's ambulances
    // query) — show the actual route the search matched, or just the first
    // active one, instead of a generic "Return trips" label with no route info.
    const routes = (d.return_trip_routes as ReturnRouteMatch[] | undefined) ?? [];
    const displayRoute = matchedReturnRoute ?? routes[0] ?? null;
    // Real registered fleet types (from the `ambulances` table, not a
    // hardcoded label) — this used to always say "Booking & Emergency"
    // regardless of what the provider actually operates, telling a patient
    // nothing about whether an ICU/Cardiac/Air Ambulance is even available here.
    const vehicleTypes = toArr(d.vehicle_types);
    const shownTypes = vehicleTypes.slice(0, 2);
    const extraCount = vehicleTypes.length - shownTypes.length;
    return (
      <div className="mb-1.5 flex flex-wrap items-center gap-1">
        {vehicleTypes.length > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-red-600/20 bg-red-600/10 px-2 py-0.5 text-[11px] font-medium text-red-700">
            <Ambulance className="h-3 w-3" />
            {shownTypes.map((t) => ambulanceTypeLabel(t)).join(" · ")}
            {extraCount > 0 ? ` +${extraCount}` : ""}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full border border-red-600/20 bg-red-600/10 px-2 py-0.5 text-[11px] font-medium text-red-700">
            <Ambulance className="h-3 w-3" /> Ambulance Service
          </span>
        )}
        {displayRoute && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
              matchedReturnRoute
                ? "border border-green-600/30 bg-green-600/10 text-green-700"
                : "bg-muted text-foreground"
            }`}
          >
            <RotateCcw className="h-3 w-3" />
            {displayRoute.fromCity && displayRoute.toCity
              ? `${displayRoute.fromCity} → ${displayRoute.toCity}`
              : "Return trip"}
            {displayRoute.discountRate ? ` · ${displayRoute.discountRate}% OFF` : ""}
            {matchedReturnRoute ? " ✓" : ""}
          </span>
        )}
      </div>
    );
  }
  
  if (item.type_code === 9) {
    const date = d.event_date ? new Date(String(d.event_date)) : null;
    const validDate = date && !isNaN(date.getTime());
    const isPaid = d.is_paid === 1 || d.is_paid === "1" || Number(d.registration_fee) > 0;
    const fee = d.registration_fee ? Number(d.registration_fee) : 0;
    // Card used to show the raw fee only, never the discount the details
    // page's Camp Schedule section already applies — the two disagreed
    // (confirmed live: card said ₹100, details page correctly said ₹90).
    const discountPct = Number(d.discount_percentage) || 0;
    const hasDiscount = isPaid && discountPct > 0 && fee > 0;
    const finalFee = hasDiscount ? Math.round(fee * (1 - discountPct / 100)) : fee;

    // Parse disease_types / free_services (may be JSON string or array)
    const parseTags = (v: any): string[] => {
      if (!v) return [];
      if (Array.isArray(v)) return v.map(String).filter(Boolean);
      try { const p = JSON.parse(String(v)); return Array.isArray(p) ? p.map(String).filter(Boolean) : []; }
      catch { return String(v).split(",").map(s => s.trim()).filter(Boolean); }
    };
    const diseaseTags = parseTags(d.disease_types).slice(0, 2);
    const serviceTags = parseTags(d.free_services).slice(0, 2);
    const allTags = [...new Set([...diseaseTags, ...serviceTags])].slice(0, 3);

    const orgType = d.organizer_type || d.organization_type;

    // No upcoming camp, but a recent one within the organizer's grace window
    // (backend: visibility_grace_days) — this is the ONLY reason such a
    // listing is on the grid at all (see getListings()'s auto-hide filter),
    // so the badge must say that plainly instead of "Upcoming camp", which
    // would wrongly imply there's something live to register for right now.
    const isRecentlyHosted = !validDate && !!item.recently_hosted;

    return (
      <div className="mb-1.5 flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1">
          {/* Date badge */}
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
            isRecentlyHosted
              ? "border-slate-400/30 bg-slate-500/10 text-slate-600"
              : "border-green-600/20 bg-green-600/10 text-green-700"
          }`}>
            <CalendarHeart className="h-3 w-3" />
            {validDate
              ? date!.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
              : isRecentlyHosted ? "Recently Hosted" : "Upcoming camp"}
          </span>
          {/* Free / Paid — skipped for a recently-hosted-only card, there's
              nothing currently open to register/pay for */}
          {!isRecentlyHosted && (isPaid ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">
              ₹{finalFee}
              {hasDiscount && <span className="font-normal line-through opacity-60">₹{fee}</span>}
            </span>
          ) : (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">Free</span>
          ))}
          {/* Organizer type */}
          {orgType && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700 capitalize">{String(orgType)}</span>
          )}
        </div>
        {/* Service/disease tags */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {allTags.map(tag => (
              <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{tag}</span>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (item.type_code === 4) {
    return (
      <div className="mb-1.5 flex flex-wrap items-center gap-1">
        <span className="inline-flex items-center gap-1 rounded-full border border-pink-600/20 bg-pink-600/10 px-2 py-0.5 text-[11px] font-medium text-pink-700">
          <HeartHandshake className="h-3 w-3" /> HomeCare Services
        </span>
        {toArr(d.services).slice(0, 1).map((s) => (
          <span key={s} className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium line-clamp-1">{s}</span>
        ))}
      </div>
    );
  }
  return null;
}

export default function ListingCardV2({ item, matchedReturnRoute }: Props) {
  const meta = TYPE_META[item.type_code] ?? { label: "Listing", slug: "listing", gradient: "from-primary to-primary", accent: "primary" };
  const href = `/${meta.slug}/${item.slug || item.id}`;
  const uiConfig = useUIConfig();
  const CatIcon = TYPE_ICON[item.type_code] ?? Building2;
  const likes = useProviderLikes(item.id, item.type_code);
  const navigate = useNavigate();
  const { isAuthenticated } = usePatientAuth();
  const { requireRegistrationToBrowse } = useAuthConfig('patient');
  const [imgFailed, setImgFailed] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleNavigate = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    // Admin-toggleable (Auth Management > Patient > "Require Registration to
    // Browse Listings") — when off, anonymous visitors browse freely and the
    // registration modal is reached only via the header's own Login button,
    // never as a side effect of clicking a listing card.
    if (isAuthenticated || !requireRegistrationToBrowse) {
      navigate(href);
    } else {
      setShowAuthModal(true);
    }
  };
  const customPlaceholder = useCategoryPlaceholder(item.type_code);

  const cityName = typeof item.city === 'string' ? item.city : (item.city as any)?.name ?? '';
  const stateName = typeof item.state === 'string' ? item.state : (item.state as any)?.name ?? '';
  const areaName = item.area ?? '';
  const location = [areaName, cityName, stateName].filter(Boolean).join(", ") || "India";
  const organizerName: string = item.details?.organizer_name ?? '';

  return (
    // A real <a href> (not a <div>/<button> with an onClick navigate() call)
    // so search crawlers can discover and follow every listing card without
    // needing JS to execute — confirmed live this was the reason internal
    // browsing paths (search grid, "Similar X near you", "Explore other
    // categories") were invisible to Google's non-JS crawl pass even though
    // the destination pages themselves have good SEO. handleNavigate still
    // intercepts the click (preventDefault) for the site's own real visitors
    // exactly as before — the anonymous-visitor auth-gate (lead capture) is
    // admin-toggleable (see requireRegistrationToBrowse above), Google's
    // ability to see the link is unaffected by that toggle either way.
    <motion.a
      href={href}
      onClick={handleNavigate}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4 }}
      // Real root cause of why this took ~600ms to visibly settle no matter
      // what whileTap transition was specified (confirmed live 2026-08-21,
      // several attempts): `transition-all` in the className below put
      // `transform` under a CSS transition too, while framer-motion was
      // ALSO animating that same `transform` via inline style every frame —
      // the CSS transition kept chasing an already-moving target, compounding
      // into something much slower than either system's own configured
      // duration. Scoping the CSS transition to exclude transform (see
      // className) let framer's own fast tween actually govern the timing.
      // Also bumped scale:0.98 -> 0.95 — the smaller shrink (this codebase's
      // usual tap value elsewhere, e.g. AiScanCapture) was still too subtle
      // to notice on a card this size even once the timing was fixed.
      whileHover={{ y: -4, transition: { type: "tween", duration: 0.15 } }}
      whileTap={{ scale: 0.95, transition: { type: "tween", duration: 0.1 } }}
      className={`group overflow-hidden border transition-[background-color,border-color,box-shadow] duration-300 h-full flex flex-col cursor-pointer ${getShadowClass(uiConfig.card_shadow)}`}
      style={{
        borderRadius: `${uiConfig.card_border_radius}px`,
        borderColor: uiConfig.card_border_color || undefined,
        backgroundColor: uiConfig.card_bg,
      }}
    >
      {/* Image */}
      <div
        className="relative aspect-[16/10] shrink-0 overflow-hidden block"
        style={{
          borderTopLeftRadius: `${uiConfig.img_border_radius ?? uiConfig.card_border_radius}px`,
          borderTopRightRadius: `${uiConfig.img_border_radius ?? uiConfig.card_border_radius}px`,
          backgroundColor: uiConfig.img_placeholder_bg,
          border: uiConfig.img_border_width > 0 ? `${uiConfig.img_border_width}px solid ${uiConfig.img_border_color || '#e2e8f0'}` : undefined,
        }}
      >
  {/* A Logo was never shot as a 16:10 cover photo — forcing it through the
      same object-cover crop as a real photo always cut off part of it (a
      wide wordmark logo especially). Render it uncropped, centered, on a
      plain white plate instead; real cover/gallery photos keep the normal
      fill-crop for a consistent grid look. */}
  {item.cover_image && !imgFailed ? (
  item.cover_image_type === 'logo' ? (
  <div className="w-full h-full flex items-center justify-center bg-white p-4">
    <img
      src={item.cover_image}
      alt={item.business_name}
      loading="lazy"
      className="max-w-full max-h-full object-contain transition-transform duration-500 group-hover:scale-105"
      onError={() => setImgFailed(true)}
    />
  </div>
  ) : (
  <img
    src={item.cover_image}
    alt={item.business_name}
    loading="lazy"
    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
    onError={() => setImgFailed(true)}
    />
  )
  ) : customPlaceholder ? (
    <img src={customPlaceholder} alt={item.business_name} className="w-full h-full object-cover" />
  ) : (
    <div className={`h-full w-full flex items-center justify-center bg-gradient-to-br ${meta.gradient}`}>
    <span className="text-3xl font-bold text-white/90">{item.business_name.charAt(0)}</span>
    </div>
  )}
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); likes.toggle(); }}
            disabled={likes.toggling}
            className="flex items-center gap-1 p-1.5 bg-white/90 shadow-sm transition-colors hover:bg-white rounded-full"
            aria-label="Like"
          >
            <Heart className={`w-4 h-4 ${likes.liked ? "fill-rose-500 text-rose-500" : "text-muted-foreground"}`} />
            {likes.count > 0 && <span className="text-[10px] font-bold pr-0.5">{likes.count}</span>}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-3.5 flex-grow flex flex-col bg-white">
        <div className="flex items-center justify-between mb-2">
          <Badge
            className="text-white font-semibold text-[10px] sm:text-xs px-2 py-0.5 border-0 flex items-center gap-1 w-fit"
            style={{
              borderRadius: `${uiConfig.badge_border_radius}px`,
              backgroundColor: TYPE_BADGE_COLOR[item.type_code] ?? "hsl(152 62% 44%)",
            }}
          >
            <CatIcon className="w-3 h-3 shrink-0" strokeWidth={2.5} />
            {meta.label}
          </Badge>
          {item.rating > 0 && <RatingStars rating={item.rating} />}
        </div>

        <h3 className="text-sm font-semibold text-foreground mb-1 line-clamp-2 group-hover:text-primary transition-colors leading-snug capitalize">
          {item.business_name || organizerName || meta.label}
        </h3>
        {/* Health Camp cards show the CAMP's own name as the title (e.g. "Dr. X
            heart camp") — without this, the organizer running it is invisible
            on the card entirely. Only shown when it's actually a different
            string from the title, so it never duplicates itself when there's
            no camp yet and the title already fell back to organizer_name. */}
        {item.type_code === 9 && organizerName && organizerName !== item.business_name && (
          <p className="text-xs text-muted-foreground -mt-0.5 mb-1.5 line-clamp-1">
            by {organizerName}
          </p>
        )}
        <TypeStrip item={item} matchedReturnRoute={matchedReturnRoute} />

        {item.type_code === 2 && item.highlights && item.highlights.length > 0 && (
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-healing-green bg-healing-green/10 px-2 py-0.5 rounded-full border border-healing-green/20">
              <span className="w-1.5 h-1.5 rounded-full bg-healing-green" />
              {item.highlights[0]}
            </span>
            {Number(item.experience_years) > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20">
                {Number(item.experience_years)}+ yrs
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-2">
          <MapPin className="w-3 h-3 text-primary/70 shrink-0" />
          <span className="line-clamp-1">{location}</span>
          {item.distance_km != null && (
            <span className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
              {item.distance_km} km
            </span>
          )}
        </div>

        <div className="flex-grow" />

        <div className="flex flex-wrap items-center justify-between gap-1.5 pt-2.5 border-t border-border/50">
          <div className="flex items-center gap-1 flex-wrap">
            {item.is_verified && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "hsl(152 62% 44% / 0.12)" }}>
                <ShieldCheck className="w-3 h-3 shrink-0" style={{ color: "hsl(152 62% 44%)" }} strokeWidth={2.5} />
                <span className="text-[10px] font-bold" style={{ color: "hsl(152 62% 44%)" }}>Verified</span>
              </div>
            )}
            {item.is_featured && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15">
                <span className="text-[10px] font-bold text-amber-600">Featured</span>
              </div>
            )}
          </div>
          <Button
            size="sm"
            tabIndex={-1}
            aria-hidden="true"
            className="text-[11px] h-7 px-3 font-bold shadow-md border-0 text-white shrink-0 pointer-events-none"
            style={{
              background: "var(--ui-gradient-cta)",
              borderRadius: `${uiConfig.btn_border_radius}px`,
            }}
          >
            View
            <ArrowUpRight className="w-3.5 h-3.5 ml-1" strokeWidth={2.5} />
          </Button>
        </div>
      </div>
    {showAuthModal && (
        <PatientAuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => { setShowAuthModal(false); navigate(href); }}
        />
      )}
    </motion.a>
  );
}

